"use client"

import { format } from "date-fns"
import { EyeIcon, CalendarIcon } from 'lucide-react'
import { useState } from "react"
import type { CashRegisterType } from "../../../../remote/types/cash-register"
import { PaginationCashfit } from "../../../components/pagination-cashfit"
import { Button } from "../../../components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Calendar } from "../../../components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { formatToBRL } from "../../../utils/formatCurrency"
import { LoadCashRegisters } from "./actions/load-cash-registers"
import { OpenCashRegister } from "./components/open-cash-register"
import { OpenInfoCashRegister } from "./components/open-info-cash-register"
import { cn } from '@/lib/utils'
import { pt } from 'date-fns/locale'

export function CashRegister() {

  const [dateRange, setDateRange] = useState<{
    dataInicial: Date | null
    dataFinal: Date | null
  }>({
    dataInicial: null,
    dataFinal: null,
  })

  const { data, reloadCashRegister, loading } = LoadCashRegisters(dateRange)
  const [modal, setModal] = useState<any>({
    item: null,
    open: false,
  })

  const handleDateChange = (field: "dataInicial" | "dataFinal", date: Date | undefined) => {
    if (date) {
      setDateRange((prev) => ({
        ...prev,
        [field]: date,
      }))
    }
  }

  const handleApplyFilter = () => {
    reloadCashRegister()
  }

  const handleClearFilter = () => {
    setDateRange({
      dataInicial: null,
      dataFinal: null,
    })
  }

  return (
    <div className="flex gap-4 flex-col">
      <header className="flex justify-between">
        <h1 className="font-bold text-2xl">Caixa</h1>
        <div className="">
          <OpenCashRegister actionReload={reloadCashRegister} />
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtros de Data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Data Inicial</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[240px] justify-start text-left font-normal border-acelerafit-green-200 hover:border-acelerafit-green-50",
                      !dateRange.dataInicial && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.dataInicial ? (
                      format(dateRange.dataInicial, "dd/MM/yyyy")
                    ) : (
                      <span>Selecione a data inicial</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    locale={pt}
                    selected={dateRange.dataInicial || undefined}
                    onSelect={(date) => handleDateChange("dataInicial", date)}
                    initialFocus
                    classNames={{
                      day_selected: "bg-orange-500 text-white hover:bg-orange-600",
                      day_today: "bg-orange-100 text-orange-900",
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Data Final</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[240px] justify-start text-left font-normal border-acelerafit-green-200 hover:border-acelerafit-green-50",
                      !dateRange.dataFinal && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.dataFinal ? (
                      format(dateRange.dataFinal, "dd/MM/yyyy")
                    ) : (
                      <span>Selecione a data final</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    locale={pt}
                    selected={dateRange.dataFinal || undefined}
                    onSelect={(date) => handleDateChange("dataFinal", date)}
                    initialFocus
                    classNames={{
                      day_selected: "bg-orange-500 text-white hover:bg-orange-600",
                      day_today: "bg-orange-100 text-orange-900",
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleApplyFilter} disabled={loading} className="bg-acelerafit-green-300 hover:bg-acelerafit-green-400 border-acelerafit-green-400 hover:border-acelerafit-green-500 text-white">
                {loading ? "Carregando..." : "Aplicar Filtro"}
              </Button>
              <Button variant="outline" onClick={handleClearFilter}>
                Limpar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {modal.open && (
        <OpenInfoCashRegister
          closeModal={() => {
            setModal({ id: null, open: false })
          }}
          cashRegister={modal.item}
          reload={reloadCashRegister}
        />
      )}

      <div className="grid grid-rows-[1fr_60px] flex-col gap-4 border-acelerafit-gray-white rounded-md border p-6 max-h-[800px] h-container-graphics">
        <Table className="">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Responsável</TableHead>
              <TableHead className="w-[180px]">Data Abertura</TableHead>
              <TableHead className="w-[120px]">Responsável fechamento</TableHead>
              <TableHead className="">Data Fechamento</TableHead>
              <TableHead className="w-[180px]">Inicial</TableHead>
              <TableHead className="w-[180px]">Final</TableHead>
              <TableHead className="w-[180px]">Troco</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="w-[100px]">Editado</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="">
            {data &&
              data.content.map((item: CashRegisterType) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.nomeResponsavel}</TableCell>
                  <TableCell>{format(item.inclusao, "dd/MM/yyyy HH:mm")}</TableCell>
                  <TableCell>{item.nomeResponsavelFechamento}</TableCell>
                  <TableCell>{item.dataFechamento && format(item.dataFechamento, "dd/MM/yyyy HH:mm")}</TableCell>
                  <TableCell className="text-sm">{formatToBRL(item?.inicio.toString() || "0.00")}</TableCell>
                  <TableCell className="text-sm">{formatToBRL(item?.saldoFinal.toString() || "0.00")}</TableCell>
                  <TableCell className="text-sm">{formatToBRL(item?.troco.toString() || "0.00")}</TableCell>
                  <TableCell>{item.statusCaixa}</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell className="text-right ">
                    <Button
                      className="text-xs bg-acelerafit-green-400 flex gap-2 hover:bg-cashfit-green-200 text-muted font-semibold"
                      onClick={() => setModal({ item: item, open: true })}
                    >
                      <EyeIcon size={14} />
                      Visualizar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <PaginationCashfit labelItem="caixas" data={data} />
      </div>
    </div>
  )
}